// =============================================================================
// rate-collection-client.ts — Client API FOURNISSEUR du BC rate-collection (S12 / D2).
// =============================================================================
// Miroir HTTP des endpoints FOURNISSEUR (apps/api/src/rate-collection/
// rate-collection-supplier.controller.ts), session opaque sous withTenant(supplier_siret).
// Comme les autres clients UI (campaign, supplier, legal), expose une INTERFACE
// (RateCollectionClient) consommée par les hooks TanStack ; l'impl HTTP réelle vient
// derrière la MÊME interface (DI seam) sans toucher l'UI.
//
// AUTORITÉ = API : le siret + le contact viennent de la SESSION (jamais du body) ; la
// validation entier>0 est RE-VALIDÉE serveur (400) ; l'idempotence d'autosave (UPSERT
// @@unique) et la garde inv.3 (409 si déjà Soumis) sont l'autorité de l'API. Ce client
// ne porte AUCUNE règle métier : il sérialise l'intention, parse la réponse, mappe les
// erreurs. AUCUNE donnée métier en localStorage (RULE_REF_006 / SF §503 / DBU §1.4.7).
//
// DTO = miroirs HTTP ASCII (PAS @jeko/db) : les enums sont répliqués en unions de
// littéraux (parité Prisma RateSeniorityLevel / CellState / SubmissionState). La SPA ne
// couple PAS au paquet DB. Horodatages = ISO 8601 (string), jamais d'objet Date ORM.

// --- Enums miroirs HTTP (parité Prisma, ASCII — accents = i18n front) ---

/** Niveau de séniorité d'une cellule (parité Prisma RateSeniorityLevel, schéma rate_collection). */
export type RateSeniorityLevelDto = 'Debutant' | 'Maitrisant' | 'Expert' | 'Referent';

/** État d'une cellule TJM (parité Prisma CellState). */
export type CellStateDto = 'Vide' | 'Saisie' | 'Soumise';

/**
 * État d'une submission (parité Prisma SubmissionState). Le contrat autosave gelé n'en
 * renvoie que Vide|Brouillon|Soumis ; les états Verrouille/ReModifiable existent au schéma
 * (cycles futurs) et sont mirrorés pour robustesse de typage de la grille.
 */
export type SubmissionStateDto =
  | 'Vide'
  | 'Brouillon'
  | 'Soumis'
  | 'Verrouille'
  | 'ReModifiable';

// --- (A) GET grille — miroir GrilleViewDto ---

/**
 * Une entrée de grille de saisie (couple profil×niveau). `profileLabel` peut être `null`
 * (profil hors surface de libellé catalog, FLAG D1 back) — l'UI affiche alors `profileCode`,
 * SANS troncature. `tjmValueEur` = valeur saisie (null si cellule Vide).
 *
 * `family` = FAMILLE du profil, donnée DÉRIVÉE du catalog FIGÉ (jointure gauche scope ∩ catalog),
 * JAMAIS un parsing de libellé d'affichage (D2-bis, OPP-03). Source de vérité UNIQUE de la
 * famille côté UI. `null` = profil absent du catalog (regroupé sous « Sans famille » à l'affichage,
 * SANS tronquer la grille). La famille ne se DEVINE pas d'une string d'affichage.
 */
export interface GrilleCellDto {
  readonly profileCode: string;
  readonly profileLabel: string | null;
  readonly family: string | null;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /**
   * A0-9 / S35-CIBLAGE-COUPLE (DBU §3.5 l.2810) : `true` = couple (profil, niveau) concerné par la
   * campagne (éditable) ; `false` = couple NON concerné ⇒ la cellule est rendue GRISÉE NON SAISISSABLE.
   * La grille garde ses 4 niveaux par profil (cardinalité inchangée) — le flag désactive, ne tronque pas.
   */
  readonly targeted: boolean;
  readonly tjmValueEur: number | null;
  readonly cellState: CellStateDto;
  readonly lastSavedAt: string | null;
  /**
   * SF7 (DBU §3.5) — RAPPEL du Cycle 1 : valeur TJM saisie au Cycle 1 pour ce couple profil×niveau
   * (même campagne, même tenant), projetée en LECTURE SEULE par l'API quand la campagne est en Cycle 2.
   * `null` en Cycle 1 (aucun cycle antérieur) OU si le couple n'a pas été renseigné au Cycle 1. Le front
   * l'AFFICHE et en DÉRIVE l'écart C1↔C2 (SF8) ; il ne le RÉÉCRIT jamais (autorité = API, shell C1 gelé).
   */
  readonly cycle1ValueEur: number | null;
}

/**
 * Vue de la grille d'une submission. `count` = borne d'AFFICHAGE DÉRIVÉE (ex. 82×4), JAMAIS
 * un plafond : l'UI ne code aucun cap/troncature/pagination dure (POINT 2 acté).
 */
export interface GrilleViewDto {
  readonly submissionId: string;
  readonly campaignId: string;
  readonly catalogVersionId: string;
  readonly submissionState: SubmissionStateDto;
  readonly count: number;
  readonly cells: readonly GrilleCellDto[];
}

// --- (B) PUT autosave — CONTRAT GELÉ (D1) ---

/** Corps de l'autosave d'UNE cellule. Entier > 0 — RE-VALIDÉ serveur (400 sinon). */
export interface AutosaveCellInput {
  readonly tjmValueEur: number;
}

/**
 * Ack d'autosave (B). `lastSavedAt` = horodatage SERVEUR (ISO 8601) — ancre l'UX
 * « enregistré à … ». Idempotent par cellule : re-PUT du même couple MET À JOUR
 * (jamais de duplicat) et renvoie un nouveau `lastSavedAt`.
 */
export interface AutosaveAckDto {
  readonly cellId: string;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly tjmValueEur: number;
  readonly lastSavedAt: string;
  readonly cellState: CellStateDto;
  readonly submissionState: SubmissionStateDto;
}

// --- (D) POST submit — miroir SubmitAckDto ---

/** Ack du submit : la submission est passée Soumis + DE.SubmissionSubmitted émis (côté API). */
export interface SubmitAckDto {
  readonly submissionId: string;
  readonly submissionState: SubmissionStateDto;
  readonly submittedAt: string;
}

// --- (A0-10) POST reopen — RÉ-OUVERTURE MÊME-CYCLE PRÉ-DEADLINE (DBU §3.5) ---

/**
 * Ack de la RÉ-OUVERTURE (A0-10) : une submission `Soumis` est ramenée à `Brouillon` ÉDITABLE (les cellules
 * saisies sont PRÉSERVÉES — inv.3). La resoumission emprunte ensuite le chemin ordinaire Brouillon→Soumis.
 * Refus (autorité API) : 409 `deadline-passed` si la date limite est dépassée ; 409 `not-reopenable` si
 * l'état courant n'est pas `Soumis`.
 */
export interface ReopenSubmissionAckDto {
  readonly submissionId: string;
  /** État après ré-ouverture : toujours `Brouillon` (grille de nouveau éditable). */
  readonly submissionState: SubmissionStateDto;
  /** État d'origine rouvert : toujours `Soumis` (dérogation même-cycle A0-10). */
  readonly reopenedFrom: SubmissionStateDto;
}

// =============================================================================
// S13 — CONTRATS GELÉS consommés (D1). Clonage (A) + EditLock (B) + DeclaredSkill (D).
// Miroirs HTTP des DTO `apps/api/src/rate-collection/dto/rate-collection.dto.ts`.
// =============================================================================

// --- (A) CLONAGE PAR COPIE (UC-F-021, G0-S13-8) ------------------------------
// POST /supplier/submissions/:submissionId/clone   (:submissionId = submission CIBLE courante)
//   body : { sourceCampaignId } ; réponse : CloneSubmissionResultDto.

/** Corps du clonage (A). `sourceCampaignId` = campagne ANTÉRIEURE participée du tenant. */
export interface CloneSubmissionInput {
  readonly sourceCampaignId: string;
}

/** Code machine STABLE d'un warning de clonage (le front discrimine sans re-parser le FR). */
export type CloneWarningCodeDto =
  | 'PROFILE_NOT_IN_CURRENT_GRID'
  | 'SKILL_NOT_IN_CURRENT_REFERENTIAL';

/** Un warning de clonage (non bloquant) : un élément de la source NON reporté dans la copie. */
export interface CloneWarningDto {
  readonly code: CloneWarningCodeDto;
  readonly profileCode: string;
  readonly skillCode: string | null;
  readonly message: string;
}

/** Résultat du clonage (A) : la submission clonée (Brouillon) + le bilan + les warnings. */
export interface CloneSubmissionResultDto {
  readonly submissionId: string;
  readonly campaignId: string;
  readonly submissionState: SubmissionStateDto;
  readonly clonedFrom: string;
  readonly cellsCloned: number;
  readonly skillsCloned: number;
  readonly warnings: readonly CloneWarningDto[];
}

// --- (B) EDITLOCK POSTGRES (G0-S13-2) ----------------------------------------
// POST/DELETE/PUT /supplier/submissions/:id/cells/:profileCode/:seniorityLevel/lock[/heartbeat]
//   (aucun body — holder = contact de session ; TTL = paramètre serveur).

/** Ack d'acquisition/heartbeat de verrou (B). `acquired=false` ⇒ cellule verrouillée par un AUTRE. */
export interface EditLockAckDto {
  readonly acquired: boolean;
  readonly holderContactId: string;
  readonly expiresAt: string;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
}

/** Ack de libération de verrou (B). Idempotent : `released=false` si aucun verrou actif. */
export interface EditLockReleaseAckDto {
  readonly released: boolean;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
}

// --- (D) DÉCLARATION COMPÉTENCES (EF-RAT-9, G0-S13-7, granularité PROFIL) -----
// GET/PUT/DELETE /supplier/submissions/:id/skills[/...]

/** Corps de déclaration d'une compétence (D). `declaredBy` vient de la SESSION (inv.1). */
export interface DeclareSkillInput {
  readonly profileCode: string;
  readonly skillCode: string;
}

/** Vue d'une compétence déclarée (D). Granularité PROFIL (G0-S13-7). */
export interface DeclaredSkillDto {
  readonly declaredId: string;
  readonly profileCode: string;
  readonly skillCode: string;
  readonly declaredAt: string;
}

/** Vue de la liste des compétences déclarées d'une submission (D). */
export interface DeclaredSkillListDto {
  readonly submissionId: string;
  readonly count: number;
  readonly skills: readonly DeclaredSkillDto[];
}

// --- S13 (D4 SEAM) LECTURES front du clonage (F1) + référentiel compétences (F2) ----
// Miroirs HTTP des DTO `apps/api/src/rate-collection/dto/rate-collection.dto.ts` (seam gelé
// DEV_API-S13-D4) : alimentent le sélecteur du clone-panel (F1) et les checkboxes du
// skills-panel (F2). LECTURES PURES (aucune mutation) ; siret/scope viennent de la session.

/**
 * (F1) Une campagne ANTÉRIEURE participée par le tenant (source possible d'un clonage).
 * `submissionId` = la submission du tenant DANS cette campagne (= source du clone). `intitule`
 * = libellé d'affichage résolu côté API (jamais dérivé d'une string). HORS la submission courante.
 */
export interface PriorCampaignDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly cycleNumber: number;
  readonly submissionId: string;
  /**
   * S35-CLONE-SKILLS-UX #5 (DBU §3.5 l.2840 : « … avec intitulé, date et statut »). STATUT = état de la
   * submission du tenant DANS cette campagne. Affiché dans le sélecteur de clonage (le fournisseur voit ce
   * qu'il s'apprête à copier).
   */
  readonly submissionState: SubmissionStateDto;
  /**
   * S35-CLONE-SKILLS-UX #5 (DBU l.2840) : DATE d'AFFICHAGE de la campagne source (ISO 8601). `null` tant
   * que la surface campaign ne l'expose pas côté API (FLAG ORCH). Le front l'affiche si présente.
   */
  readonly date: string | null;
}

/** (F1) Liste des campagnes antérieures participées, triée par intitulé (autorité API). */
export interface PriorCampaignListDto {
  readonly submissionId: string;
  readonly count: number;
  readonly campaigns: readonly PriorCampaignDto[];
}

/** (F2) Une compétence déclarable (référentiel courant) : code machine + libellé d'affichage. */
export interface ProfileSkillDto {
  readonly skillCode: string;
  readonly libelle: string;
  /**
   * S35-CLONE-SKILLS-UX #4 (DBU §3.5 l.2826) : DESCRIPTIF riche de la compétence (aide survol/clic). `null`
   * tant que la surface catalog ne l'expose pas côté API (FLAG ORCH). Le front rend « aucune aide » sans
   * casser l'affordance d'aide.
   */
  readonly description: string | null;
}

/** (F2) Un profil + ses compétences DÉCLARABLES (référentiel COURANT du catalog figé). */
export interface SkillByProfileDto {
  readonly profileCode: string;
  /**
   * REM-A #4 (DBU §3.5) : LIBELLÉ d'affichage du profil (Profile.libelle), propagé par l'API. Affiché en
   * légende du fieldset ; `profileCode` reste la clé technique de la coche déclarée.
   */
  readonly libelle: string;
  readonly skills: readonly ProfileSkillDto[];
}

/**
 * (F2) Référentiel de compétences DÉCLARABLES par profil pour la version de catalogue FIGÉE
 * de la submission. Un profil sans compétence déclarable est OMIS (liste allégée). LECTURE PURE :
 * ne distingue PAS les compétences déjà déclarées (servies par `listSkills`).
 */
export interface SkillReferentialByProfileListDto {
  readonly submissionId: string;
  readonly catalogVersionId: string;
  readonly count: number;
  readonly profiles: readonly SkillByProfileDto[];
}

// --- (SF6) GET fiche profil — pop-up de saisie (DBU §3.5) ---------------------
// GET /supplier/submissions/:submissionId/profiles/:profileCode
// Miroir HTTP de `ProfileDetailDto` (apps/api). Fiche de LECTURE ouverte au clic sur un
// profil de la grille : descriptif + niveaux disponibles + missions/prérequis. Chargée à
// la DEMANDE (lazy) — n'alourdit pas la grille 82×4.

/**
 * (SF6) Fiche profil de LECTURE pour la pop-up de saisie (DBU §3.5). Aucune donnée saisissable.
 *
 * `missions` / `prerequis` sont `string | null` : `null` tant qu'aucune source ne les peuple
 * (le parser Excel ne les extrait PAS — contrat de classeur figé, DDD §3.4.3 ; l'édition admin
 * ne les écrit pas encore). L'UI les rend alors HONNÊTEMENT « non renseigné », JAMAIS inventés.
 * `description` / `libelle` / `famille` peuvent aussi être `null` (profil hors surface catalog) :
 * l'UI dégrade proprement sans troncature.
 */
export interface ProfileDetailDto {
  readonly profileCode: string;
  readonly libelle: string | null;
  readonly famille: string | null;
  readonly sousFamille: string | null;
  readonly description: string | null;
  readonly availableLevels: readonly string[];
  readonly missions: string | null;
  readonly prerequis: string | null;
}

// --- Erreurs applicatives typées ---

/**
 * Genre d'erreur applicative remontée par l'API fournisseur, mappé pour un message UX
 * explicite. `already-submitted` = 409 (inv.3, submission déjà Soumis / verrouillée) ;
 * `invalid-value` = 400 (garde entier>0 serveur) ; `forbidden` = 403 (CSRF absent/non
 * concordant OU sous-garde inv.2 du submit) ; `unauthorized` = 401 (session absente/
 * expirée) ; `not-found` = 404 (submission hors tenant) ; `generic` = autre.
 *
 * S13 — discriminateurs 409 par `code` machine STABLE (corps `{statusCode, code, message}`,
 * D1 §3 ; le front les mappe SANS re-parser le message FR) :
 *   `deadline-passed`               = SUBMISSION_DEADLINE_PASSED (G0-S13-3, lock-out) ;
 *   `cell-locked`                   = CELL_LOCKED (G0-S13-2, cellule verrouillée par un autre) ;
 *   `clone-no-prior-participation`  = CLONE_NO_PRIOR_PARTICIPATION (G0-S13-4, pas de participation).
 */
export type RateCollectionApiErrorKind =
  | 'already-submitted'
  | 'invalid-value'
  | 'forbidden'
  | 'unauthorized'
  | 'not-found'
  | 'deadline-passed'
  | 'cell-locked'
  | 'clone-no-prior-participation'
  | 'not-reopenable'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API fournisseur (HTTP 4xx/5xx). L'UI l'affiche telle
 * quelle ; elle ne réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet
 * un libellé UX ciblé (ex. bannière « déjà soumise » sur 409) sans parser le message.
 */
export class RateCollectionApiError extends Error {
  public readonly kind: RateCollectionApiErrorKind;

  /**
   * A0-11 / S35-VERROU-PROFIL (DBU §3.5 l.2948) : NOM affichable du détenteur d'un verrou concurrent
   * (`cell-locked`), résolu côté API depuis l'annuaire supplier-reference (Contact.nomComplet). `null`
   * si l'API n'a pas pu le résoudre ⇒ le badge retombe sur un libellé générique, JAMAIS un UUID. Absent
   * pour les autres `kind`. PII tenant-scoped : jamais journalisé, affiché uniquement dans le badge.
   */
  public readonly holderDisplayName: string | null;

  public constructor(
    message: string,
    kind: RateCollectionApiErrorKind = 'generic',
    holderDisplayName: string | null = null,
  ) {
    super(message);
    this.name = 'RateCollectionApiError';
    this.kind = kind;
    this.holderDisplayName = holderDisplayName;
  }
}

/**
 * Contrat du client FOURNISSEUR rate-collection — miroir des endpoints D1. Aucune règle
 * métier ici : transitions/validations sont l'autorité de l'API (TenantInterceptor session,
 * withTenant, garde entier>0, idempotence, inv.3). L'UI relaie l'intention.
 */
export interface RateCollectionClient {
  /** (A) GET grille profil×niveau de la submission (dérivée scope figé ∩ catalog). */
  getGrille(submissionId: string): Promise<GrilleViewDto>;

  /**
   * (B) Autosave idempotent d'UNE cellule (PUT). Le siret/contact viennent de la session
   * (jamais du body). Re-PUT du même couple = pas de doublon (UPSERT @@unique).
   */
  autosaveCell(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
    input: AutosaveCellInput,
  ): Promise<AutosaveAckDto>;

  /** (D) submit() : Brouillon→Soumis sous garde inv.2 (API). 409 si déjà Soumis. */
  submit(submissionId: string): Promise<SubmitAckDto>;

  /**
   * (A0-10) RÉ-OUVERTURE MÊME-CYCLE : une submission `Soumis` redevient `Brouillon` ÉDITABLE tant que la
   * deadline n'est pas atteinte (cellules préservées). 409 `deadline-passed` si la date limite est dépassée ;
   * 409 `not-reopenable` si l'état courant n'est pas `Soumis`. Le siret/contact viennent de la session.
   */
  reopenSubmission(submissionId: string): Promise<ReopenSubmissionAckDto>;

  // --- S13 (D4 SEAM) Lectures front : clonage (F1) + référentiel compétences (F2) ---

  /**
   * (F1) GET campagnes ANTÉRIEURES participées par le tenant (HORS la submission courante).
   * Peuple le sélecteur du clone-panel. La liste est résolue sous le tenant de session.
   */
  getPriorCampaigns(submissionId: string): Promise<PriorCampaignListDto>;

  /**
   * (F2) GET référentiel de compétences DÉCLARABLES par profil (version de catalogue figée
   * de la submission). Peuple les checkboxes du skills-panel.
   */
  getSkillReferential(
    submissionId: string,
  ): Promise<SkillReferentialByProfileListDto>;

  /**
   * (SF6) GET fiche profil de LECTURE (pop-up de saisie, DBU §3.5). Chargée à la DEMANDE au clic
   * sur un profil de la grille : descriptif + niveaux disponibles + missions/prérequis. 404 si le
   * profil est absent de la version de catalogue figée. Le siret vient de la session.
   */
  getProfileDetail(
    submissionId: string,
    profileCode: string,
  ): Promise<ProfileDetailDto>;

  // --- S13 (A) Clonage par copie inter-campagne (G0-S13-4/8) ---

  /**
   * Clone la submission d'une campagne ANTÉRIEURE participée (`sourceCampaignId`) vers la
   * submission CIBLE (`submissionId`, campagne courante). Copie INDÉPENDANTE. 409
   * `clone-no-prior-participation` si aucune participation antérieure. Warnings non bloquants.
   */
  cloneSubmission(
    submissionId: string,
    input: CloneSubmissionInput,
  ): Promise<CloneSubmissionResultDto>;

  // --- S13 (B) EditLock Postgres, granularité cellule (G0-S13-2) ---

  /** Acquiert le verrou d'une cellule (focus). 409 `cell-locked` si déjà verrouillée par un autre. */
  acquireLock(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): Promise<EditLockAckDto>;

  /** Libère le verrou d'une cellule (perte de focus / beforeunload). Idempotent. */
  releaseLock(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): Promise<EditLockReleaseAckDto>;

  /** Heartbeat : repousse l'expiration du verrou détenu par l'appelant. */
  heartbeatLock(
    submissionId: string,
    profileCode: string,
    seniorityLevel: RateSeniorityLevelDto,
  ): Promise<EditLockAckDto>;

  // --- S13 (D) Déclaration de compétences, granularité PROFIL (G0-S13-7) ---

  /** Liste les compétences déclarées d'une submission. */
  listSkills(submissionId: string): Promise<DeclaredSkillListDto>;

  /** Déclare (upsert idempotent) une compétence pour un profil. */
  declareSkill(
    submissionId: string,
    input: DeclareSkillInput,
  ): Promise<DeclaredSkillDto>;

  /** Retire une compétence déclarée. Idempotent. */
  removeSkill(
    submissionId: string,
    profileCode: string,
    skillCode: string,
  ): Promise<{ readonly removed: boolean }>;
}
